import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Send, Edit2, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  draft: "bg-slate-700 text-slate-200",
  scheduled: "bg-amber-600/20 text-amber-300 border-amber-600/40",
  published: "bg-emerald-600/20 text-emerald-300 border-emerald-600/40",
  failed: "bg-red-600/20 text-red-300 border-red-600/40",
};

const blankPostForm = { title: "", body: "", image_url: "", tags: "", categories: "", status: "draft", scheduled_at: "", telegram_channel_ids: [] };

function PostDialog({ open, onOpenChange, editing, channels, onSaved }) {
  const [form, setForm] = useState(blankPostForm);

  useEffect(() => {
    if (editing) {
      setForm({
        ...editing,
        tags: (editing.tags || []).join(", "),
        categories: (editing.categories || []).join(", "),
        scheduled_at: editing.scheduled_at?.slice(0, 16) || "",
        telegram_channel_ids: editing.telegram_channel_ids || [],
      });
    } else { setForm(blankPostForm); }
  }, [editing, open]);

  const submit = async () => {
    if (!form.title || !form.body) { toast.error("Titolo e corpo sono obbligatori"); return; }
    const payload = {
      title: form.title, body: form.body, image_url: form.image_url || null,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      categories: form.categories.split(",").map(t => t.trim()).filter(Boolean),
      status: form.status,
      scheduled_at: form.status === "scheduled" && form.scheduled_at
        ? new Date(form.scheduled_at).toISOString() : null,
      telegram_channel_ids: form.telegram_channel_ids,
      platforms: ["telegram"],
    };
    try {
      if (editing) { await api.patch(`/posts/${editing.id}`, payload); toast.success("Post aggiornato"); }
      else { await api.post("/posts", payload); toast.success("Post creato"); }
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const toggleCh = (id) => {
    setForm(f => ({
      ...f,
      telegram_channel_ids: f.telegram_channel_ids.includes(id)
        ? f.telegram_channel_ids.filter(x => x !== id)
        : [...f.telegram_channel_ids, id]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifica post" : "Nuovo post"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Titolo</Label>
            <Input data-testid="post-title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" />
          </div>
          <div>
            <Label>Corpo del post</Label>
            <Textarea data-testid="post-body" value={form.body} rows={8} onChange={e=>setForm({...form, body:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>URL Immagine</Label>
              <Input data-testid="post-image" value={form.image_url} onChange={e=>setForm({...form, image_url:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="https://..." />
            </div>
            <div>
              <Label>Stato</Label>
              <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                <SelectTrigger data-testid="post-status" className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectItem value="draft">Bozza</SelectItem>
                  <SelectItem value="scheduled">Programmato</SelectItem>
                  <SelectItem value="published">Pubblicato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.status === "scheduled" && (
            <div>
              <Label>Data programmazione</Label>
              <Input type="datetime-local" data-testid="post-schedule" value={form.scheduled_at} onChange={e=>setForm({...form, scheduled_at:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tag (virgola)</Label>
              <Input data-testid="post-tags" value={form.tags} onChange={e=>setForm({...form, tags:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="seriea, juventus" />
            </div>
            <div>
              <Label>Categorie</Label>
              <Input data-testid="post-categories" value={form.categories} onChange={e=>setForm({...form, categories:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="news, transfers" />
            </div>
          </div>
          <div>
            <Label>Canali Telegram</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {channels.length === 0 && <div className="text-xs text-slate-500 col-span-2">Nessun canale configurato.</div>}
              {channels.map(c => (
                <button key={c.id} type="button" onClick={()=>toggleCh(c.id)} data-testid={`select-channel-${c.id}`}
                  className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${form.telegram_channel_ids.includes(c.id) ? "bg-blue-600/20 border-blue-500/50 text-blue-200" : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700"}`}>
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[10px] font-mono">{c.chat_id}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="post-save" className="bg-blue-600 hover:bg-blue-700">{editing ? "Salva" : "Crea post"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Posts() {
  const [posts, setPosts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/posts"), api.get("/telegram/channels")]);
    setPosts(p.data); setChannels(c.data);
  };
  useEffect(() => { load(); }, []);

  const publish = async (id) => {
    try { const r = await api.post(`/posts/${id}/publish`); toast.success(r.data.status === "published" ? "Pubblicato" : "Pubblicazione fallita"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/posts/${id}`); toast.success("Eliminato"); load();
  };

  return (
    <div data-testid="posts-page">
      <PageHeader title="Contenuti" subtitle="Crea, programma e pubblica post Telegram.">
        <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if(!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-post-btn" className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-1" /> Nuovo post</Button>
          </DialogTrigger>
        </Dialog>
      </PageHeader>
      <PostDialog open={open} onOpenChange={(o)=>{ setOpen(o); if(!o) setEditing(null); }} editing={editing} channels={channels} onSaved={load} />
      <PageContent>
        <div className="bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-xs uppercase tracking-wider text-slate-500 font-mono">
              <tr>
                <th className="text-left py-3 px-4">Titolo</th>
                <th className="text-left py-3 px-4">Stato</th>
                <th className="text-left py-3 px-4">Programmato</th>
                <th className="text-left py-3 px-4">Tag</th>
                <th className="text-right py-3 px-4">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {posts.length === 0 && (
                <tr><td colSpan="5" className="py-10 text-center text-slate-500">Nessun post.</td></tr>
              )}
              {posts.map(p => (
                <tr key={p.id} className="border-t border-slate-800 hover:bg-slate-950/50" data-testid={`post-row-${p.id}`}>
                  <td className="py-3 px-4">
                    <div className="text-white font-medium">{p.title}</div>
                    <div className="text-xs text-slate-500 truncate max-w-md">{p.body?.slice(0, 80)}</div>
                  </td>
                  <td className="px-4"><Badge variant="outline" className={`${statusColors[p.status]} border`}>{p.status}</Badge></td>
                  <td className="px-4 text-xs text-slate-400 font-mono">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString("it-IT") : "—"}</td>
                  <td className="px-4"><div className="flex flex-wrap gap-1">{(p.tags||[]).slice(0,3).map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">#{t}</span>)}</div></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={()=>publish(p.id)} data-testid={`publish-${p.id}`} className="h-8 text-blue-400 hover:text-blue-300 hover:bg-blue-600/10"><Send size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={()=>{ setEditing(p); setOpen(true); }} data-testid={`edit-${p.id}`} className="h-8"><Edit2 size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={()=>remove(p.id)} data-testid={`delete-${p.id}`} className="h-8 text-red-400 hover:text-red-300 hover:bg-red-600/10"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageContent>
    </div>
  );
}
